<script setup>
import { ref, onMounted } from "vue";
import CardBoxModal from "@/Components/CardBoxModal.vue";
import BaseIcon from "@/Components/BaseIcon.vue";
import BreezeInput from '@/Components/Input.vue';
import BreezeLabel from '@/Components/Label.vue';
import BreezeCheckbox from '@/Components/Checkbox.vue';
import { Head, Link, useForm } from '@inertiajs/inertia-vue3';
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import ApplicationLogoFull from '@/Components/ApplicationLogoFull.vue';
import BreezeDropdown from '@/Components/Dropdown.vue';
import BreezeDropdownLink from '@/Components/DropdownLink.vue';
import BaseButton from "@/Components/BaseButton.vue";
import Footer from '@/Components/Footer.vue';
import { Inertia } from '@inertiajs/inertia';
import { Icon } from '@iconify/vue';
import QRCode from 'qrcode';
import {
    mdiCog,
    mdiCellphoneArrowDown,
    mdiMonitorArrowDown,
    mdiChevronDown,
    mdiQrcode,
    mdiSchool,
    mdiAccountGroup,
    mdiBriefcaseAccount
} from "@mdi/js";

const props = defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
    laravelVersion: String,
    phpVersion: String,
    appVersion: String,
    contentWelcome: String,
    accessStudents: Boolean,
    accessParents: Boolean,
    heroImage: String,
    appUrlQr: String,
    qrImage: String
});

const form = useForm({
    email: '',
    nombre: '',
    phone: '',
    comment: '',
    recibirinfo: false
});
const isModalActive = ref(false);
const isQrModalActive = ref(false);
const isIosModalActive = ref(false);
const qrDataUrl = ref('');

// Detección de Dispositivo y Estado PWA
const isMobile = ref(false);
const isAppInstalled = ref(false);
const deferredPrompt = ref(null);
const isIos = ref(false);

const generateQr = async () => {
    if (props.qrImage) {
        qrDataUrl.value = props.qrImage;
        return;
    }
    const url = props.appUrlQr ? props.appUrlQr : window.location.origin;
    try {
        qrDataUrl.value = await QRCode.toDataURL(url, {
            width: 280,
            margin: 2,
            color: {
                dark: '#0f172a',
                light: '#ffffff'
            }
        });
    } catch (err) {
        console.error('Error generando QR:', err);
    }
};

const checkDeviceAndPwa = () => {
    if (typeof window !== 'undefined') {
        isMobile.value = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
        isIos.value = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        isAppInstalled.value = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    }
};

const installApp = async () => {
    if (deferredPrompt.value) {
        deferredPrompt.value.prompt();
        const { outcome } = await deferredPrompt.value.userChoice;
        if (outcome === 'accepted') {
            isAppInstalled.value = true;
        }
        deferredPrompt.value = null;
    } else if (isIos.value) {
        isIosModalActive.value = true;
    } else {
        alert('Para instalar la App en tu pantalla de inicio:\n1. Pulsa los 3 puntos (menú) de tu navegador.\n2. Selecciona "Instalar aplicación" o "Añadir a la pantalla principal".');
    }
};

onMounted(() => {
    generateQr();
    checkDeviceAndPwa();

    window.addEventListener('resize', checkDeviceAndPwa);

    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        deferredPrompt.value = e;
    });

    window.addEventListener('appinstalled', () => {
        isAppInstalled.value = true;
        deferredPrompt.value = null;
    });
});
</script>

<template>
    <Head title="Bienvenido" />

    <!-- Modal -->
    <CardBoxModal v-model="isModalActive" title="Matrícula en línea">
        <p>Información del <b>padre de familia / apoderado</b></p>
        <!--<p>This is sample modal</p>-->
        <div>
            <BreezeLabel for="nombre" value="Nombre" />
            <BreezeInput id="name" type="text" class="mt-1 block w-full" v-model="form.nombre" required autofocus autocomplete="name" />
        </div>
        <div class="mt-4">
            <BreezeLabel for="nivel" value="Nivel escolar" />
            <BreezeInput id="inicial" type="checkbox" class="mt-1 ml-2" v-model="form.inicial" required autocomplete="inicial" /> Inicial
            <BreezeInput id="primaria" type="checkbox" class="mt-1 ml-2" v-model="form.primaria" required autocomplete="primaria" /> Primaria
            <BreezeInput id="secundaria" type="checkbox" class="mt-1 ml-2" v-model="form.secundaria" required autocomplete="secundaria" /> Secundaria
        </div>
        <div class="mt-4">
            <BreezeLabel for="phone" value="Teléfono" />
            <BreezeInput id="phone" type="text" class="mt-1 block w-full" v-model="form.phone" required autocomplete="phone" />
        </div>
        <div class="mt-4">
            <BreezeLabel for="email" value="Email" />
            <BreezeInput id="email" type="email" class="mt-1 block w-full" v-model="form.email" required autocomplete="mail" />
        </div>

        <div class="mt-4">
            <BreezeLabel for="comment" value="Comentario" />
            <BreezeInput id="comment" type="text" class="mt-1 block w-full" v-model="form.comment" required autocomplete="comment" />
        </div>
        <!--<div class="block mt-4">
            <label class="flex items-center">
                <BreezeCheckbox name="remember" v-model:checked="form.recibirinfo" />
                <span class="ml-2 text-sm text-gray-600">Recibir novedades?</span>
            </label>
        </div>-->
    </CardBoxModal>
    <!-- Fin modal -->
    <!-- Modal Código QR PWA -->
    <CardBoxModal v-model="isQrModalActive" title="Acceso Móvil / App">
        <div class="flex flex-col items-center text-center p-2 space-y-4">
            <div class="p-3 bg-white border-2 border-slate-200 rounded-2xl shadow-sm">
                <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR PWA" class="w-56 h-56 object-contain" />
            </div>
            <div>
                <h4 class="font-bold text-slate-800 text-base">Escanea con la cámara de tu smartphone</h4>
                <p class="text-xs text-slate-500 mt-1 max-w-sm">
                    Abre la plataforma desde tu celular e instálala directamente en tu pantalla de inicio como App.
                </p>
            </div>
            <div class="w-full pt-2">
                <a 
                    :href="appUrlQr || '#'" 
                    target="_blank" 
                    class="block w-full py-2.5 px-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs rounded-xl transition-colors truncate shadow-sm"
                >
                    {{ appUrlQr || 'Abrir enlace directo' }}
                </a>
            </div>
        </div>
    </CardBoxModal>

    <!-- Modal Guía para iOS Safari -->
    <CardBoxModal v-model="isIosModalActive" title="Instalar en tu iPhone / iPad">
        <div class="text-left p-2 space-y-4">
            <p class="text-sm text-slate-700">Para agregar Unens Education a tu pantalla de inicio desde Safari:</p>
            <ol class="text-xs text-slate-600 space-y-2.5 list-decimal list-inside bg-slate-50 p-4 rounded-xl border border-slate-200">
                <li>Pulsa el botón <b>Compartir</b> de Safari (icono con una flecha hacia arriba).</li>
                <li>Desplaza la lista hacia abajo y pulsa <b>"Añadir a la pantalla de inicio"</b>.</li>
                <li>Pulsa <b>"Añadir"</b> en la esquina superior derecha.</li>
            </ol>
            <p class="text-xs text-slate-500 text-center">¡Listo! Se creará un acceso directo como aplicación en tu pantalla.</p>
        </div>
    </CardBoxModal>

    <div class="min-h-screen bg-white">
        <!--Nav-->
        <header>
            <nav id="header" class="sticky w-full bg-white border-b border-gray-200">
                <div class="mx-auto sm:px-6 lg:px-8 px-4">
                    <div class="flex justify-between h-20">
                        <div class="flex items-center">
                            <div class="shrink-0 flex items-center h-12">
                                <ApplicationLogoFull class="max-h-12 w-auto object-contain" />
                            </div>
                        </div>
                        <div class="flex items-center">
                            <Link v-if="$page.props.auth.user" :href="route('dashboard')" class="text-sm text-gray-700 underline">
                            <button id="navAction" class="btn btn-rose">
                                Intranet
                            </button>
                            </Link>
                            <template v-else>
                                <div class="sm:flex sm:items-center sm:ml-6">
                                    <!-- Settings Dropdown -->
                                    <div class="ml-3 relative">
                                        <BreezeDropdown align="right" width="48">
                                            <template #trigger>
                                                <button class="btn btn-rose btn-ico w-full justify-center" >
                                                    <span class="mr-2">Intranet</span>
                                                    <BaseIcon :path="mdiChevronDown" />
                                                </button>
                                            </template>

                                            <template #content>
                                                <BreezeDropdownLink v-if="accessStudents" :href="route('login')" as="button">
                                                    Estudiantes
                                                </BreezeDropdownLink>
                                                <BreezeDropdownLink v-if="accessParents" :href="route('login')" as="button">
                                                    Padres
                                                </BreezeDropdownLink>
                                                <BreezeDropdownLink :href="route('login')" as="button">
                                                    Colaboradores
                                                </BreezeDropdownLink>
                                            </template>
                                        </BreezeDropdown>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </nav>
        </header>
        <!--Hero-->
        <section>
            <!-- <div class="fixed z-100 bottom-0 right-0">
            <div id="installbutton" ref="installbutton">
                <BaseButton :icon="mdiCellphoneArrowDown" color="info" rounded-full icon-size="20" label="Instalar APP"
                class="lg:hidden m-5 p-2" />
                <BaseButton :icon="mdiMonitorArrowDown" color="info" rounded-full icon-size="20" label="Instalar APP"
                    class="hidden lg:flex m-5 p-2"/>
            </div>

            <div class="fixed z-50 bottom-0 right-0">
            <BaseButton :icon="mdiCog" color="info" rounded-full icon-size="w-20 h-20"
                class="m-5 p-2"/>
        </div>
        </div> -->

            <!-- Content Welcome : -->
            <!-- <div class="md:w-10/12 w-full">
            <p class="md:text-lg text-xs">Con más de 38 años en el rubro educativo podemos afianzarle una correcta enseñanza.
            </p>
            <h1 class="md:my-4 mb-3 font-bold md:text-6xl text-4xl">
                Somos Colegios <span class="text-rose-600">Monserrat</span>
            </h1>
            <h2 class="md:text-3xl text-base mb-4">
                Te invitamos a ser parte de nuestro circulo de estudios
            </h2>
            <button @click="isModalActive = true" class="btn sm:btn-small lg:btn-lg btn-rose">
                        Matrícula en línea
            </button>
        </div> -->

            <div class="md:h-[calc(100vh_-_91px)] h-[calc(100vh_-_67px)]">
                <div class="container mx-auto px-4 sm:px-6 lg:px-8 h-[calc(100%_-_40px)]">
                    <div class="grid md:grid-cols-2 grid-cols-1 h-full content-center items-center gap-8 py-4">
                        <div class="col-span-1 flex flex-col justify-center h-full space-y-6">
                            <!-- Contenido HTML configurable -->
                            <div v-html="contentWelcome ? contentWelcome : ''"></div>

                            <!-- Botones de Intranet (Estudiante, Apoderado, Colaborador) a futuro se mostrará -->
                            <!-- <div class="pt-2">
                                <div class="flex flex-wrap items-center gap-3">
                                    <Link v-if="accessStudents"
                                        :href="route('login')" 
                                        class="group flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-50/80 hover:bg-rose-600 text-rose-700 hover:text-white border border-rose-200 hover:border-rose-600 rounded-xl transition-all duration-200 shadow-xs hover:shadow-md text-center font-bold text-sm"
                                    >
                                        <BaseIcon :path="mdiSchool" class="w-5 h-5 transition-transform group-hover:scale-110" />
                                        <span>Estudiantes</span>
                                    </Link>
                                    <Link v-if="accessParents"
                                        :href="route('login')" 
                                        class="group flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50/80 hover:bg-blue-600 text-blue-700 hover:text-white border border-blue-200 hover:border-blue-600 rounded-xl transition-all duration-200 shadow-xs hover:shadow-md text-center font-bold text-sm"
                                    >
                                        <BaseIcon :path="mdiAccountGroup" class="w-5 h-5 transition-transform group-hover:scale-110" />
                                        <span>Apoderados</span>
                                    </Link>
                                    <Link 
                                        :href="route('login')" 
                                        class="group flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-50/80 hover:bg-emerald-600 text-emerald-700 hover:text-white border border-emerald-200 hover:border-emerald-600 rounded-xl transition-all duration-200 shadow-xs hover:shadow-md text-center font-bold text-sm"
                                    >
                                        <BaseIcon :path="mdiBriefcaseAccount" class="w-5 h-5 transition-transform group-hover:scale-110" />
                                        <span>Colaboradores</span>
                                    </Link>
                                </div>
                            </div> -->
                        </div>

                        <div class="col-span-1 flex flex-col items-center justify-center h-full relative">
                            <div class="md:mt-0 mt-4 w-full flex justify-center">
                                <img 
                                    class="md:max-w-full md:max-h-[380px] max-h-[260px] object-contain drop-shadow-sm transition-all" 
                                    :src="heroImage || 'img/hero.png'" 
                                    alt="Ilustración Unens Education" 
                                />
                            </div>

                            <!-- Acceso QR (Computadora) o Botón Instalar APP (Celular) -->
                            <div class="w-full flex justify-end mt-4 sm:mt-2">
                                <!-- En Computadora / Desktop: Mostrar Código QR -->
                                <div 
                                    v-if="!isMobile"
                                    class="hidden md:inline-flex items-center gap-3 p-2 pr-3.5 bg-white border border-slate-200/90 hover:border-rose-400 rounded-2xl shadow-xs hover:shadow-md transition-all cursor-pointer group"
                                    @click="isQrModalActive = true"
                                    title="Clic para ampliar código QR"
                                >
                                    <div class="w-14 h-14 bg-slate-50 p-1 rounded-xl border border-slate-200/80 flex items-center justify-center shrink-0 shadow-2xs group-hover:scale-105 transition-transform">
                                        <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR PWA" class="w-full h-full object-contain" />
                                        <BaseIcon v-else :path="mdiQrcode" class="w-8 h-8 text-slate-400" />
                                    </div>
                                    <div class="text-left">
                                        <div class="flex items-center gap-1.5">
                                            <span class="text-xs font-bold text-slate-800 tracking-tight">Acceso Móvil</span>
                                            <span class="text-[9px] font-extrabold uppercase bg-rose-100 text-rose-700 px-1.5 py-0.2 rounded-full">App</span>
                                        </div>
                                        <p class="text-[11px] text-slate-500 font-medium leading-tight mt-0.5">Escanear código QR</p>
                                        <span class="text-[10px] text-rose-600 font-semibold inline-flex items-center gap-0.5 mt-0.5 group-hover:underline">
                                            Ampliar QR
                                        </span>
                                    </div>
                                </div>

                                <!-- En Celular / Móvil: Mostrar botón "Instalar APP" si no está instalada -->
                                <div 
                                    v-else-if="!isAppInstalled" 
                                    class="w-full sm:w-auto flex justify-center md:hidden"
                                >
                                    <button 
                                        type="button" 
                                        @click="installApp"
                                        class="w-full inline-flex items-center justify-center gap-2.5 px-5 py-3 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white font-bold text-sm rounded-2xl shadow-lg hover:shadow-xl active:scale-95 transition-all"
                                    >
                                        <BaseIcon :path="mdiCellphoneArrowDown" class="w-5 h-5 animate-pulse" />
                                        <span>Instalar APP en el móvil</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="fixed bottom-0 bg-gray-100 w-full z-40">
                    <div class="w-full flex justify-center">
                        <div class="container mx-auto">
                            <div class="grid grid-cols-1 justify-center py-2">
                                <div class="text-center">
                                    <p class="text-sm md:text-base">&copy; Unens Education {{ appVersion }} <a href="https://unens.com/" target="_blank"> - unens.com</a></p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>



    <!-- <footer class="bg-white rounded-lg shadow m-4 dark:bg-gray-800">
        <div class="text-center w-full mx-auto max-w-screen-xl p-4 justify-center">
            <span class="text-sm text-gray-500 sm:text-center dark:text-gray-400">© 2023 <a href="https://unens.com/" target="_blank" class="hover:underline">Unens.com</a>. Unens Edu 2.0.0
            </span>
        </div>
    </footer> -->
</template>
